import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addToast } from '@heroui/react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/common/ui/PageHeader';
import ServiceCard from '../../../components/common/ui/ServiceCard';
import CustomPagination from '../../../components/common/ui/CustomPagination';
import LoadingSpinner from '../../../components/common/loading/LoadingSprinner';
import { useAuth } from '../../../../context/AuthContext';
import {
  getFavoriteServices,
  removeFavoriteService,
} from '../../../../service/addFavService';
import { FEATURE_FLAGS, useFeatureFlag } from '../../../../config/featureFlags';
import {
  applyBroadcastSelection,
  isBroadcastSelectionDisabled,
  type BroadcastSelectionMap,
} from '../../../../utils/broadcastSelection';

const PAGE_SIZE = 12;

const FavoriteServices = () => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const quoteBroadcastEnabled = useFeatureFlag(FEATURE_FLAGS.quoteBroadcast);
  const [currentPage, setCurrentPage] = useState(1);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [selectedProviders, setSelectedProviders] =
    useState<BroadcastSelectionMap>(new Map());
  const [swapNotice, setSwapNotice] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ADD-FAVORITE-SERVICES', currentPage],
    queryFn: () => getFavoriteServices({ page: currentPage, limit: PAGE_SIZE }),
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
  });

  const services = useMemo(() => {
    const list = data?.data?.services ?? data?.services;
    if (!Array.isArray(list)) return [];

    console.log('FavoriteServices data:', data);

    const formattedList = list.map((service) => {
      const formattedService = {
        ...service,
        location: service?.businessLocation ? [service.businessLocation] : [],
        businessName: service?.business?.name || service?.businessName || 'N/A',
        businessSlug: service?.business?.businessSlug || '',
        business: {
          name: service?.businessName || 'N/A',
          businessSlug: service?.businessSlug || '',
        },
      };
      return formattedService;
    });
    console.log('FavoriteServices data formattedList:', formattedList);
    return formattedList as any[];
  }, [data]);

  const totalPages = useMemo(() => {
    const p = data?.data?.pages ?? data?.pages;
    return typeof p === 'number' && p > 0 ? p : 0;
  }, [data]);

  useEffect(() => {
    if (isLoading) return;
    if (services.length === 0 && currentPage > 1 && totalPages > 0) {
      if (currentPage > totalPages) {
        setCurrentPage(totalPages);
      }
    }
  }, [isLoading, services.length, currentPage, totalPages]);

  const removeMutation = useMutation({
    mutationFn: (serviceId: string) => removeFavoriteService(serviceId),
    onMutate: (serviceId) => setRemovingId(serviceId),
    onSettled: () => setRemovingId(null),
    onSuccess: async () => {
      addToast({
        title: 'Removed from favorites',
        radius: 'md',
        color: 'success',
      });
      await queryClient.invalidateQueries({
        queryKey: ['ADD-FAVORITE-SERVICES'],
      });
    },
    onError: (err: unknown) => {
      const message =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        (err as { response?: { data?: { message?: string } } }).response?.data
          ?.message
          ? String(
              (err as { response: { data: { message: string } } }).response.data
                .message
            )
          : 'Could not remove favorite. Please try again.';
      addToast({
        title: 'Remove failed',
        description: message,
        radius: 'md',
        color: 'danger',
      });
    },
  });

  const handleRemove = useCallback(
    (serviceId: string) => {
      if (!serviceId) return;
      removeMutation.mutate(serviceId);
    },
    [removeMutation]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (page >= 1 && (totalPages === 0 || page <= totalPages)) {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    [totalPages]
  );

  const handleProviderSelectChange = useCallback(
    (
      serviceId: string,
      providerId: string,
      businessId: string,
      isSelected: boolean
    ) => {
      const { selection, swapped } = applyBroadcastSelection(
        selectedProviders,
        { serviceId, providerId, businessId },
        isSelected
      );
      setSelectedProviders(selection);
      setSwapNotice(
        swapped ? 'Swapped — you can ask each business once.' : null
      );
    },
    [selectedProviders]
  );

  const isCardSelected = useCallback(
    (serviceId: string, providerId: string) =>
      selectedProviders.has(`${serviceId}|${providerId}`),
    [selectedProviders]
  );

  const isSelectionDisabled = useCallback(
    (serviceId: string, providerId: string, businessId: string) =>
      isBroadcastSelectionDisabled(
        selectedProviders,
        serviceId,
        providerId,
        businessId
      ),
    [selectedProviders]
  );

  const handleClearSelections = useCallback(() => {
    setSelectedProviders(new Map());
    setSwapNotice(null);
  }, []);

  const handleRequestQuoteForAll = useCallback(() => {
    if (selectedProviders.size === 0) return;
    const services = Array.from(selectedProviders.values()).map((s) => ({
      serviceId: s.serviceId,
      providerId: s.providerId,
    }));
    navigate('/service/broadcast-request', { state: { services } });
  }, [navigate, selectedProviders]);

  return (
    <div>
      <PageHeader
        title="Favorite Services"
        description="Services you have saved. Remove any you no longer need."
      />

      <div className="mt-6">
        {isLoading && <LoadingSpinner />}

        {!isLoading && isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {(error as Error)?.message ||
              'Failed to load favorite services. Please try again.'}
          </div>
        )}

        {!isLoading && !isError && services.length === 0 && (
          <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-gray-600">
            You have no favorite services yet. Browse search and tap the heart
            to add some.
          </div>
        )}

        {!isLoading && !isError && services.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
              {services.map((service) => (
                <div
                  key={service.serviceId}
                  className="mb-2 rounded-lg border border-secondary/30 bg-white shadow-sm transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-lg"
                >
                  <ServiceCard
                    data={service}
                    mode="favoriteDashboard"
                    onRemoveFavorite={handleRemove}
                    isRemoveLoading={
                      removingId === service.serviceId &&
                      removeMutation.isPending
                    }
                    isBroadcastSelectable={
                      quoteBroadcastEnabled &&
                      (service.bookingType || 'SLOT').toUpperCase() === 'QUOTE'
                    }
                    isSelected={isCardSelected(
                      service.serviceId,
                      service.providerId
                    )}
                    onSelectChange={handleProviderSelectChange}
                    isSelectionDisabled={isSelectionDisabled(
                      service.serviceId,
                      service.providerId,
                      service.businessId
                    )}
                  />
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-end items-center py-6 mt-4">
                <CustomPagination
                  page={currentPage}
                  initialPage={1}
                  total={totalPages}
                  size="md"
                  onChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>

      {quoteBroadcastEnabled && selectedProviders.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 laptop:left-[260px] z-40 bg-white border-t border-gray-200 shadow-lg p-4 pr-24 md:p-6 md:pr-28">
          {swapNotice && (
            <div className="max-w-7xl mx-auto mb-2 text-sm text-gray-600">
              {swapNotice}
            </div>
          )}
          <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="text-lg font-semibold text-gray-800">
                {selectedProviders.size} selected
              </div>
              {selectedProviders.size < 5 && (
                <div className="text-sm text-gray-600">
                  (Up to 5 recommended)
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleClearSelections}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Clear
              </button>
              <button
                onClick={handleRequestQuoteForAll}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-semibold"
              >
                Request quotes from all
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FavoriteServices;
